import React from 'react';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ChatBubbleOutline as ChatIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';

function formatRelativeTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function ChatSidebar({
  sessions,
  activeSessionId,
  collapsed,
  onToggleCollapse,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  // Server-backed history (audit P-12) has no per-item delete endpoint: hide it.
  hideDelete = false,
}) {
  if (collapsed) {
    return (
      <Box
        className="chat-sidebar chat-sidebar--collapsed"
        sx={{
          width: 52,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          py: 1,
          gap: 1,
        }}
      >
        <IconButton
          size="small"
          onClick={onToggleCollapse}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          sx={{ color: 'text.secondary' }}
        >
          <ChevronRightIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={onNewChat}
          aria-label="New chat"
          title="New chat"
          sx={{ color: 'text.secondary' }}
        >
          <AddIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      className="chat-sidebar"
      sx={{
        width: 260,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          pt: 1,
          pb: 0.5,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <IconButton
          size="small"
          onClick={onToggleCollapse}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          sx={{ color: 'text.secondary' }}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            textTransform: 'none',
            borderRadius: '20px',
            justifyContent: 'flex-start',
            py: 1,
          }}
        >
          New chat
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {sessions.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', px: 2, py: 1 }}
          >
            No conversations yet
          </Typography>
        ) : (
          <List dense disablePadding>
            {sessions.map((session) => {
              const selected = session.id === activeSessionId;
              return (
                <ListItemButton
                  key={session.id}
                  selected={selected}
                  onClick={() => onSelectSession(session.id)}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: '10px',
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  <ChatIcon
                    sx={{
                      fontSize: 18,
                      mr: 1.5,
                      color: selected ? 'primary.main' : 'text.secondary',
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={session.title || 'New chat'}
                    secondary={formatRelativeTime(session.updatedAt)}
                    primaryTypographyProps={{
                      noWrap: true,
                      fontSize: '0.875rem',
                      fontWeight: selected ? 600 : 400,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                    }}
                  />
                  {!hideDelete && (
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteSession(session.id);
                      }}
                      sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                      aria-label="Delete chat"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}
